/**
 * The anchored comment rail, shared by both surfaces.
 *
 * The geometry is the design mockup's, and three of its rules are structural:
 *
 *   · **The track never scrolls.** It is an `overflow: hidden` window onto one
 *     absolutely-positioned layer that is translated by `-scrollTop` as the
 *     course scrolls. Card positions are computed once in unscrolled space —
 *     following the reader costs one transform, not a re-layout.
 *
 *   · **Anchors are translated with a measured origin delta** — the frame's
 *     top minus the track's top — never used raw. See useRailGeometry.
 *
 *   · **Everything above the track is `flex: none` and outside it.** The
 *     pinned Overall section lives before the track element, so expanding it
 *     moves the track and re-measures the delta — it never shifts a card
 *     inside the coordinate space. (The first version had the overall cards
 *     *inside* the positioning context, which offset every anchor by whatever
 *     height they happened to have.)
 *
 * Threads whose block is not in the DOM — the runtime rebuilds per lesson —
 * keep working from the "Elsewhere in the course" strip at the bottom. That
 * strip is this app's own addition; the mockups assume one lesson.
 */

import { useMemo, useRef, useState } from 'react';
import { api, type ProjectTag, type ThreadWire, type ThreadsWire } from '../../api';
import { Composer, type Mentionable } from '../Composer';
import { mono, monoPlain, RADIUS } from '../../theme';
import { ds } from '../../ds';
import { spliceByBlockOrder, stackCards, type RailItem } from './stack';
import { useRailGeometry } from './useRailGeometry';
import { ThreadCard, type RailCaps } from './ThreadCard';
import type { CourseState } from './useCourseBus';

export type ComposeTarget = { blockId: string; anchorLabel: string };

const CONNECTOR = { width: 14, inset: 16, top: 20 } as const;

function connectorColour(selected: boolean, resolved: boolean): string {
  if (selected) return 'var(--primary)';
  if (resolved) return 'var(--border-subtle)';
  return 'var(--primary-soft)';
}

export function CommentRail({
  projectId,
  wire,
  visible,
  people,
  tags,
  caps,
  course,
  frameRef,
  selected,
  composeFor,
  filter,
  query = '',
  header,
  loadFailed = false,
  onRetry,
  guardWrite,
  onCompose,
  onSelect,
  onChanged,
  composerPlaceholder,
  composerSubmitLabel,
  overallAddLabel
}: {
  projectId: string;
  wire: ThreadsWire | null;
  /** Threads to show, already run through the page's filters. */
  visible: ThreadWire[];
  people: Mentionable[];
  tags: ProjectTag[];
  caps: RailCaps;
  course: CourseState;
  frameRef: React.RefObject<HTMLIFrameElement | null>;
  selected: string | null;
  composeFor: ComposeTarget | null;
  filter: 'open' | 'resolved' | 'all';
  query?: string;
  /** The page's own toolbar row(s) — filter pills and friends. */
  header: React.ReactNode;
  loadFailed?: boolean;
  onRetry?: () => void;
  /** Gate for writes. Return false to swallow the action (e.g. the reviewer
   *  identify step); the guard is expected to open whatever dialog it needs. */
  guardWrite?: () => boolean;
  onCompose: (target: ComposeTarget | null) => void;
  onSelect: (blockId: string) => void;
  onChanged: () => void;
  composerPlaceholder: string;
  composerSubmitLabel: string;
  overallAddLabel: string;
}) {
  const { Icon } = ds();
  const track = useRef<HTMLDivElement | null>(null);
  const { originDelta, heights } = useRailGeometry({
    frame: frameRef as React.RefObject<HTMLElement | null>,
    track: track as React.RefObject<HTMLElement | null>
  });

  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [overallOpen, setOverallOpen] = useState(true);
  const [composingOverall, setComposingOverall] = useState(false);

  const me = wire?.me ?? null;

  /** Content-space top and document order per block, from the overlay. */
  const { topFor, orderFor } = useMemo(() => {
    const tops = new Map<string, number>();
    const order = new Map<string, number>();
    course.boxes.forEach((b, i) => {
      tops.set(b.id, b.top);
      order.set(b.id, i);
    });
    return { topFor: tops, orderFor: order };
  }, [course.boxes]);

  const collapsedFor = (t: ThreadWire) => !!t.resolvedAt && !expanded.includes(t.id);
  const toggleCollapsed = (id: string) =>
    setExpanded((x) => (x.includes(id) ? x.filter((e) => e !== id) : [...x, id]));

  const overall = visible.filter((t) => t.scope === 'overall');

  const { placed, elsewhere } = useMemo(() => {
    const blockThreads = visible.filter((t) => t.scope === 'block' && t.blockId);
    const onScreen = blockThreads.filter((t) => topFor.has(t.blockId!));
    const offScreen = blockThreads.filter((t) => !topFor.has(t.blockId!));

    type Queued = { id: string; blockId: string; thread: ThreadWire | null };
    const ordered: Queued[] = [...onScreen]
      .sort((a, b) => (orderFor.get(a.blockId!) ?? 0) - (orderFor.get(b.blockId!) ?? 0))
      .map((t) => ({ id: t.id, blockId: t.blockId!, thread: t }));

    // The open composer joins the same queue at its block's position, so
    // opening it pushes every card below it down instead of covering one.
    const queue =
      composeFor && topFor.has(composeFor.blockId)
        ? spliceByBlockOrder(
            ordered,
            { id: '__composer', blockId: composeFor.blockId, thread: null },
            (q) => orderFor.get(q.blockId) ?? Number.MAX_SAFE_INTEGER
          )
        : ordered;

    const items: RailItem[] = queue.map((q) => ({
      id: q.id,
      blockTop: topFor.get(q.blockId) ?? 0,
      height:
        heights[q.id] ??
        (q.thread
          ? caps.estimate(q.thread, collapsedFor(q.thread)) + (replyTo === q.id ? 108 : 0)
          : caps.composerEstimate)
    }));

    return { placed: stackCards(items, originDelta), elsewhere: offScreen };
    // collapsedFor closes over `expanded`; list it rather than the helper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, topFor, orderFor, composeFor, heights, originDelta, replyTo, expanded, caps]);

  const anchoredThreads = visible.filter(
    (t) => t.scope === 'block' && t.blockId && topFor.has(t.blockId)
  );

  const composerAnchored = !!composeFor && topFor.has(composeFor.blockId);
  const composerTop = placed.find((p) => p.id === '__composer')?.top ?? 0;

  const empty =
    !loadFailed &&
    anchoredThreads.length === 0 &&
    overall.length === 0 &&
    elsewhere.length === 0 &&
    !composeFor;

  const emptyCopy = query.trim()
    ? {
        title: 'No comments match that search.',
        body: 'Try a reviewer’s name, or clear the filters.'
      }
    : filter === 'resolved'
      ? { title: 'Nothing resolved yet.', body: 'Threads you resolve stay here, greyed out.' }
      : {
          title: 'No comments here yet.',
          body: 'Hover a block in the course and use its Comment button to start one.'
        };

  const [elsewhereOpen, setElsewhereOpen] = useState(false);

  const cardProps = (t: ThreadWire) => ({
    thread: t,
    people,
    tags,
    me,
    projectId,
    caps,
    selected: selected === t.blockId && !!t.blockId,
    carriedOver: !!wire && t.round < wire.project.round && !t.resolvedAt,
    collapsed: collapsedFor(t),
    replying: replyTo === t.id,
    guardWrite,
    onToggleCollapsed: () => toggleCollapsed(t.id),
    onReply: () => {
      if (guardWrite && !guardWrite()) return;
      setReplyTo(replyTo === t.id ? null : t.id);
    },
    onChanged,
    onSelect: () => t.blockId && onSelect(t.blockId)
  });

  return (
    <>
      {header}

      {/* ── Overall, pinned above the track ────────────────────────────── */}
      <div
        style={{
          flex: 'none',
          margin: '10px 12px 0',
          borderRadius: RADIUS.card,
          border: '1px solid var(--primary-soft)',
          background: 'var(--primary-soft)',
          overflow: 'hidden'
        }}
      >
        <button
          type="button"
          onClick={() => setOverallOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            width: '100%',
            padding: '9px 12px',
            background: 'none',
            border: 0,
            cursor: 'pointer'
          }}
        >
          <Icon name="globe" size={12} style={{ color: 'var(--primary)' }} />
          <span style={{ ...mono, fontSize: 9 }}>Overall · whole course</span>
          <span style={{ flex: 1 }} />
          {overall.length > 0 && (
            <span style={{ ...mono, fontSize: 9 }}>
              {overall.length} thread{overall.length === 1 ? '' : 's'}
            </span>
          )}
          <Icon name={overallOpen ? 'chevron-up' : 'chevron-down'} size={12} />
        </button>

        {overallOpen && (
          <div style={{ padding: '0 8px 8px', display: 'grid', gap: 8 }}>
            {overall.map((t) => (
              <ThreadCard key={t.id} {...cardProps(t)} />
            ))}

            {composingOverall && me ? (
              <div
                style={{
                  padding: 10,
                  borderRadius: RADIUS.card,
                  border: '1px solid var(--primary)',
                  background: 'var(--surface-card)'
                }}
              >
                <p style={{ ...mono, marginBottom: 8 }}>Overall · whole course</p>
                <Composer
                  people={people}
                  tags={caps.tagsInComposer ? tags : []}
                  placeholder="Pacing, tone, structure — anything not tied to one block."
                  submitLabel={composerSubmitLabel}
                  projectId={projectId}
                  onCancel={() => setComposingOverall(false)}
                  onSubmit={async (body, mentions, tagId, attachmentIds) => {
                    await api.threads.create(projectId, {
                      scope: 'overall',
                      body,
                      mentions,
                      tagId: tagId ?? undefined,
                      attachmentIds
                    });
                    setComposingOverall(false);
                    onChanged();
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (guardWrite && !guardWrite()) return;
                  setComposingOverall(true);
                }}
                style={{
                  ...monoPlain,
                  padding: '8px 10px',
                  borderRadius: RADIUS.control,
                  border: '1px dashed var(--border-strong, var(--border-subtle))',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                + {overallAddLabel}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── The track: a clipped window onto one translated layer ──────── */}
      <div
        ref={track}
        style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', marginTop: 10 }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            transform: `translateY(${-course.scrollTop}px)`
          }}
        >
          {placed.map((p) => {
            if (p.id === '__composer') return null;

            const t = anchoredThreads.find((x) => x.id === p.id);
            if (!t) return null;
            return (
              <div
                key={t.id}
                style={{
                  position: 'absolute',
                  left: CONNECTOR.inset,
                  right: CONNECTOR.inset,
                  top: p.top
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: -CONNECTOR.inset,
                    top: CONNECTOR.top,
                    width: CONNECTOR.width,
                    height: 1,
                    background: connectorColour(selected === t.blockId, !!t.resolvedAt)
                  }}
                />
                <ThreadCard {...cardProps(t)} />
              </div>
            );
          })}
        </div>

        {/* The composer is a direct child of the track, not of the translated
            layer, and keeps one key whether or not its block is measurable —
            so switching lessons mid-draft repositions it instead of
            unmounting it and losing the text. Anchored, its top mirrors the
            layer's translation by hand; unanchored — the runtime rebuilt the
            DOM for another lesson — it docks to the foot of the track with
            its frozen label saying where it belongs. */}
        {composeFor && me && (
          <div
            key="__composer"
            data-card="__composer"
            style={{
              position: 'absolute',
              left: CONNECTOR.inset,
              right: CONNECTOR.inset,
              ...(composerAnchored
                ? { top: composerTop - course.scrollTop }
                : { bottom: 12 })
            }}
          >
            {composerAnchored && (
              <span
                style={{
                  position: 'absolute',
                  left: -CONNECTOR.inset,
                  top: CONNECTOR.top,
                  width: CONNECTOR.width,
                  height: 1,
                  background: 'var(--primary)'
                }}
              />
            )}
            <div
              style={{
                padding: 12,
                borderRadius: RADIUS.card,
                border: '1px solid var(--primary)',
                boxShadow: '0 0 0 3px var(--primary-soft)',
                background: 'var(--surface-card)'
              }}
            >
              <p style={{ ...mono, marginBottom: 8 }}>
                {composeFor.anchorLabel || 'New comment'}
              </p>
              <Composer
                people={people}
                tags={caps.tagsInComposer ? tags : []}
                placeholder={composerPlaceholder}
                submitLabel={composerSubmitLabel}
                projectId={projectId}
                onCancel={() => onCompose(null)}
                onSubmit={async (body, mentions, tagId, attachmentIds) => {
                  await api.threads.create(projectId, {
                    scope: 'block',
                    blockId: composeFor.blockId,
                    anchorLabel: composeFor.anchorLabel,
                    body,
                    mentions,
                    tagId: tagId ?? undefined,
                    attachmentIds
                  });
                  onCompose(null);
                  onChanged();
                }}
              />
            </div>
          </div>
        )}

        {loadFailed && !wire && (
          <p style={{ ...monoPlain, lineHeight: 1.6, padding: '16px 16px' }}>
            The comments would not load.{' '}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  font: 'inherit',
                  border: 0,
                  background: 'none',
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Try again
              </button>
            )}
          </p>
        )}

        {empty && (
          <div style={{ padding: '24px 18px' }}>
            <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', margin: 0 }}>
              {emptyCopy.title}
            </p>
            <p style={{ ...monoPlain, lineHeight: 1.6, marginTop: 6 }}>{emptyCopy.body}</p>
          </div>
        )}
      </div>

      {/* ── Elsewhere: threads whose lesson is not in the DOM ──────────── */}
      {elsewhere.length > 0 && (
        <div
          style={{
            flex: 'none',
            maxHeight: '40%',
            display: 'flex',
            flexDirection: 'column',
            borderTop: '1px solid var(--border-subtle)'
          }}
        >
          <button
            type="button"
            onClick={() => setElsewhereOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 14px',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              flex: 'none'
            }}
          >
            <span style={mono}>Elsewhere in the course · {elsewhere.length}</span>
            <span style={{ flex: 1 }} />
            <Icon name={elsewhereOpen ? 'chevron-down' : 'chevron-up'} size={12} />
          </button>
          {elsewhereOpen && (
            <div style={{ overflowY: 'auto', padding: '0 12px 12px', display: 'grid', gap: 10 }}>
              {elsewhere.map((t) => (
                <ThreadCard key={t.id} {...cardProps(t)} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
